<script>
import { IP_PATTERN } from "../util";
import { handleError } from "@/utils/error";
import { mapFields } from "vuex-map-fields";
import { mapState } from "vuex";
import { chunkArray } from "@/utils";
import { DaysEnum } from "../enums";

export default {
  name: "DeviceForm",

  data() {
    return {
      valid: false,
      min: 1024,
      max: 49151,
      ipRules: [
        v => {
          if (!v || v.length < 1) {
            return this.$t("modules.devices.ipAddresses.error.required");
          } else if (v.length > 0 && !v.every(ip => IP_PATTERN.test(ip))) {
            return this.$t("modules.devices.ipAddresses.error.invalid");
          }
          return true;
        }
      ],
      timeoutRules: [
        v => {
          if (!v && v !== 0) {
            return this.$t("validation.required", [
              this.$t("modules.devices.timeout")
            ]);
          }

          const integerRegex = /^[0-9]+$/;
          if (!integerRegex.test(v)) {
            return this.$t("modules.devices.timeoutIntegerErrorMessages");
          }

          if (v < 0 || v > 1440) {
            return this.$t("modules.devices.timeoutRangeErrorMessage", [
              0,
              1440
            ]);
          }

          return true;
        }
      ]
    };
  },

  computed: {
    ...mapFields("devices", [
      "editedItem.name",
      "editedItem.pairing_id",
      "editedItem.ipAddresses",
      "editedItem.fromPort",
      "editedItem.toPort",
      "editedItem.isEnabled",
      "editedItem.isServer",
      "editedItem.isAutoUpgrade",
      "editedItem.allowedDays",
      "editedItem.allowedHours",
      "editedItem.fixedUpgradeVersion",
      "editedItem.timeout"
    ]),
    ...mapState("devices", [
      "devices",
      "dialog",
      "formTitle",
      "editedItem",
      "editedIndex"
    ]),
    ...mapState("loader", ["is_loading", "is_loading_global"]),
    chuckedDays() {
      let days = Object.entries(DaysEnum).map(([key, value]) => ({
        text: key.toLowerCase(),
        value: value
      }));
      let chunkedDays = chunkArray(days, 2);

      return chunkedDays;
    },
    chunkedHours() {
      let chunkedHours = [];
      let hours = [...Array(24).keys()];

      let formattedHours = hours.map(hour => {
        let text = hour < 10 ? `0${hour}:00` : `${hour}:00`;
        let value = hour;
        return { text, value };
      });

      chunkedHours = chunkArray(formattedHours, 6);

      return chunkedHours;
    }
  },

  methods: {
    removeIP(item) {
      let index = this.ipAddresses.indexOf(item);
      this.$store.commit("devices/removeIP", index);
    },

    switchItem() {
      if (!this.fromPort || this.fromPort < this.min) {
        this.$store.commit("devices/updateFromPort", this.min);
      } else if (!this.toPort || this.toPort > this.max) {
        this.$store.commit("devices/updateToPort", this.max);
      } else if (parseInt(this.fromPort) > parseInt(this.toPort)) {
        let from = this.fromPort;
        let to = this.toPort;
        this.$store.commit("devices/updateFromPort", to);
        this.$store.commit("devices/updateToPort", from);
      }
    },

    getUpdatedDeviceData() {
      let upgradePolicy = null;

      if (this.isServer && this.isAutoUpgrade) {
        upgradePolicy = {
          is_auto_upgrade_enabled: this.isAutoUpgrade,
          allowed_days: this.allowedDays,
          allowed_hours: this.allowedHours,
          idle_timeout_in_minutes: parseInt(this.timeout),
          fixed_version: this.fixedUpgradeVersion
        };
      }

      let data = {
        name: this.name,
        pairing_id: this.pairing_id,
        type: this.type,
        ipAddresses: this.ipAddresses,
        fromPort: this.fromPort,
        toPort: this.toPort,
        isEnabled: this.isEnabled,
        isServer: this.isServer,
        upgradePolicy: upgradePolicy
      };

      return data;
    },

    save() {
      if (!this.$refs.deviceForm.validate()) {
        return;
      }
      this.$store.dispatch("loader/startLoading");

      let payload = this.getUpdatedDeviceData();

      if (this.editedIndex > -1) {
        payload.id = this.devices[this.editedIndex].id;
        this.$store
          .dispatch("devices/update", payload)
          .then(this.close)
          .catch(error => {
            handleError(error);
          });
      } else {
        this.$store
          .dispatch("devices/add", payload)
          .then(this.close)
          .catch(error => {
            handleError(error);
          });
      }
    },
    close() {
      this.$store.dispatch("devices/toggleDialog", false).then(() => {
        this.$store.dispatch("devices/clearForm");
        this.$store.commit("devices/updateIndex", -1);
        this.$refs.deviceForm.resetValidation();
      });
    }
  }
};
</script>

<template>
  <v-card>
    <v-form v-model="valid" ref="deviceForm" @submit.prevent="save">
      <v-card-title>
        <span class="headline" id="devices__form-title">{{ formTitle }}</span>
      </v-card-title>
      <v-card-text>
        <v-layout column wrap>
          <v-flex>
            <v-text-field
              v-model="name"
              :label="$t('modules.devices.name')"
              id="devices__form-name"
              :rules="[
                v =>
                  !!v || $t('validation.required', [$t('modules.devices.name')])
              ]"
            ></v-text-field>
          </v-flex>
          <v-flex>
            <v-text-field
              v-model="pairing_id"
              :label="$t('modules.devices.pairing_id')"
              id="devices__form-pairing_id"
              :rules="[
                v =>
                  !!v ||
                  $t('validation.required', [$t('modules.devices.pairing_id')])
              ]"
            ></v-text-field>
          </v-flex>
          <v-flex>
            <v-checkbox
              v-model="isEnabled"
              :label="$t('modules.devices.isEnabled')"
              id="devices__form-enabled"
              color="primary"
            ></v-checkbox>
          </v-flex>
          <v-flex>
            <v-switch
              v-model="isServer"
              :label="$t('modules.devices.server')"
              id="devices__form-ip-is-server"
              color="primary"
            ></v-switch>
          </v-flex>
          <v-flex v-if="isServer">
            <v-combobox
              v-model="ipAddresses"
              :label="$t('modules.devices.ipAddresses.label')"
              chips
              multiple
              deletable-chips
              id="devices__form-ip-addresses"
              :rules="ipRules"
            >
              <template v-slot:selection="data">
                <v-chip
                  :input-value="data.selected"
                  close
                  @click:close="removeIP(data.item)"
                >
                  <strong>{{ data.item }}</strong>
                </v-chip>
              </template>
            </v-combobox>
          </v-flex>
          <v-layout wrap v-if="isServer">
            <v-flex xs12>
              <span>{{ $t("modules.devices.ports") }}</span>
            </v-flex>
            <v-flex sm6 md6>
              <v-text-field
                v-model="fromPort"
                :label="$t('modules.devices.fromPort')"
                id="devices__form-from-port"
                type="number"
                :min="min"
                :max="max"
                @change="switchItem"
                :rules="[v => v >= min || $t('validation.more', [min])]"
              ></v-text-field>
            </v-flex>
            <v-flex sm6 md6>
              <v-text-field
                v-model="toPort"
                :label="$t('modules.devices.toPort')"
                id="devices__form-to-port"
                type="number"
                :min="min"
                :max="max"
                @change="switchItem"
                :rules="[v => v <= max || $t('validation.less', [max])]"
              ></v-text-field>
            </v-flex>
          </v-layout>
          <v-layout v-if="isServer" row class="mx-1">
            <v-switch
              v-model="isAutoUpgrade"
              id="devices__form-is-auto-upgrade"
              :label="$t('modules.devices.autoUpgrade')"
            >
            </v-switch>
            <v-tooltip bottom id="devices__form-is-auto-upgrade__tooltip">
              <template v-slot:activator="{ on, attrs }">
                <v-icon color="primary" class="ml-2" v-bind="attrs" v-on="on"
                  >mdi-information</v-icon
                >
              </template>
              <span id="devices__form-is-auto-upgrade__tooltip-text"
                >{{ $t("modules.devices.autoUpgradeTooltip") }}
              </span>
            </v-tooltip>
          </v-layout>
          <v-layout column justify-center v-if="isServer && isAutoUpgrade">
            <v-flex class="mb-4">
              <v-subheader class="ml-0 pl-0" id="devices__form-allowed-days">
                {{ $t("modules.devices.allowedDays") }}
                <v-tooltip bottom id="devices__form-allowed-days__tooltip">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      color="primary"
                      class="ml-2"
                      v-bind="attrs"
                      v-on="on"
                      >mdi-information</v-icon
                    >
                  </template>
                  <span id="devices__form-allowed-days__tooltip-text">
                    {{ $t("modules.devices.allowedDaysTooltip") }}
                  </span>
                </v-tooltip>
              </v-subheader>
              <v-layout row wrap class="pl-3">
                <v-flex
                  v-for="(chunk, index) in chuckedDays"
                  :key="index"
                  xs12
                  md3
                >
                  <v-checkbox
                    v-for="day in chunk"
                    :key="day.value"
                    v-model="allowedDays"
                    :label="day.text"
                    :value="day.value"
                    class="text-capitalize"
                    dense
                    hide-details
                    :rules="[v => v.length > 0]"
                    :id="`devices__form-allowed-days__checkbox-${day.value}`"
                  >
                  </v-checkbox>
                </v-flex>
                <v-flex
                  xs12
                  class="red--text"
                  v-if="allowedDays.length === 0"
                  id="devices__form-allowed-days__error-message"
                >
                  {{ $t("modules.devices.allowedDaysErrorMessage") }}
                </v-flex>
              </v-layout>
            </v-flex>

            <v-flex class="mb-4">
              <v-subheader class="ml-0 pl-0" id="devices__form-allowed-hours">
                {{ $t("modules.devices.allowedHours") }}
                <v-tooltip bottom id="devices__form-allowed-hours__tooltip">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      color="primary"
                      class="ml-2"
                      v-bind="attrs"
                      v-on="on"
                      >mdi-information</v-icon
                    >
                  </template>
                  <span id="devices__form-allowed-hours__tooltip-text">
                    {{ $t("modules.devices.allowedHoursTooltip") }}
                  </span>
                </v-tooltip>
              </v-subheader>

              <v-layout row wrap class="pl-3">
                <v-flex
                  v-for="(chuckHours, index) in chunkedHours"
                  :key="index"
                  xs12
                  md3
                >
                  <v-checkbox
                    v-for="hour in chuckHours"
                    :key="hour.value"
                    v-model="allowedHours"
                    :label="hour.text"
                    dense
                    hide-details
                    :value="hour.value"
                    :rules="[v => v.length > 0]"
                    :id="`devices__form-allowed-days__checkbox-${hour.value}`"
                  ></v-checkbox>
                </v-flex>
                <v-flex
                  xs12
                  class="red--text"
                  v-if="allowedHours.length === 0"
                  id="devices__form-allowed-days__error-message"
                >
                  {{ $t("modules.devices.allowedHoursErrorMessage") }}
                </v-flex>
              </v-layout>
            </v-flex>
            <v-flex class="mt-2">
              <v-subheader
                class="ml-0 pl-0 mb-0 pb-0"
                id="devices__form-timeout"
                style="height:auto"
              >
                {{ $t("modules.devices.timeout") }}
                <v-tooltip
                  bottom
                  id="devices__form-timeout__tooltip"
                  class="mb-0 pb-0"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      color="primary"
                      class="ml-2"
                      v-bind="attrs"
                      v-on="on"
                      >mdi-information</v-icon
                    >
                  </template>
                  <span id="devices__form-timeout__tooltip-text">
                    {{ $t("modules.devices.timeoutTooltip") }}
                  </span>
                </v-tooltip>
              </v-subheader>
              <v-text-field
                v-model="timeout"
                :rules="timeoutRules"
                id="devices__form-timeout_field"
                class="mt-0 pt-0"
              >
              </v-text-field>
            </v-flex>
          </v-layout>
        </v-layout>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          color="secondary"
          text
          elevation="1"
          @click="close"
          id="devices__cancel"
          >{{ $t("actions.cancel") }}</v-btn
        >
        <v-btn
          color="secondary"
          text
          elevation="1"
          type="submit"
          :loading="is_loading"
          :disabled="is_loading_global"
          id="devices__save"
          >{{
            editedIndex === -1 ? $t("actions.add") : $t("actions.save")
          }}</v-btn
        >
      </v-card-actions>
    </v-form>
  </v-card>
</template>
